// Chakra imports
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import React, { useEffect } from 'react';
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdStrikethroughS,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdCode,
  MdUndo,
  MdRedo,
  MdLink,
  MdImage,
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdFormatAlignJustify,
} from 'react-icons/md';

export default function RichTextEditor({ value, onChange, placeholder, ...rest }) {
  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const bg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const toolbarBg = useColorModeValue('gray.50', 'navy.700');
  const hoverBg = useColorModeValue('gray.100', 'navy.600');
  const activeBg = useColorModeValue('gray.200', 'navy.500');
  const placeholderColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const codeBg = useColorModeValue('gray.100', 'navy.700');
  const linkColor = useColorModeValue('brand.500', 'brand.300');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      TextStyle,
      Placeholder.configure({
        placeholder: placeholder || 'Write your content here...',
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  // Update editor content when value prop changes externally
  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt('Image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Box
      {...rest}
      border={`1px solid ${borderColor}`}
      borderRadius='16px'
      bg={bg}
      overflow='hidden'>
      {/* Toolbar */}
      <Flex
        bg={toolbarBg}
        p='8px'
        borderBottom={`1px solid ${borderColor}`}
        wrap='wrap'
        gap='4px'
        align='center'>
        {/* Text Formatting */}
        <ButtonGroup size='sm' isAttached>
          <IconButton
            icon={<Icon as={MdFormatBold} />}
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            aria-label='Bold'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatItalic} />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            aria-label='Italic'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatUnderlined} />}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            aria-label='Underline'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdStrikethroughS} />}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            aria-label='Strikethrough'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
        </ButtonGroup>

        <Divider orientation='vertical' height='24px' />

        {/* Headings */}
        <Menu>
          <MenuButton
            as={Button}
            size='sm'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}>
            H{editor.isActive('heading', { level: 1 }) ? '1' : editor.isActive('heading', { level: 2 }) ? '2' : editor.isActive('heading', { level: 3 }) ? '3' : ''}
          </MenuButton>
          <MenuList bg={bg} borderColor={borderColor}>
            <MenuItem
              onClick={() => editor.chain().focus().setParagraph().run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Paragraph
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Heading 1
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Heading 2
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Heading 3
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Heading 4
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Heading 5
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
              bg={bg}
              _hover={{ bg: hoverBg }}>
              Heading 6
            </MenuItem>
          </MenuList>
        </Menu>

        <Divider orientation='vertical' height='24px' />

        {/* Lists */}
        <ButtonGroup size='sm' isAttached>
          <IconButton
            icon={<Icon as={MdFormatListBulleted} />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            aria-label='Bullet List'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatListNumbered} />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            aria-label='Numbered List'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatQuote} />}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            aria-label='Quote'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdCode} />}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            aria-label='Code Block'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
        </ButtonGroup>

        <Divider orientation='vertical' height='24px' />

        {/* Alignment */}
        <ButtonGroup size='sm' isAttached>
          <IconButton
            icon={<Icon as={MdFormatAlignLeft} />}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            aria-label='Align Left'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatAlignCenter} />}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            aria-label='Align Center'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatAlignRight} />}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            aria-label='Align Right'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdFormatAlignJustify} />}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            isActive={editor.isActive({ textAlign: 'justify' })}
            aria-label='Align Justify'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
        </ButtonGroup>

        <Divider orientation='vertical' height='24px' />

        {/* Links and Images */}
        <ButtonGroup size='sm' isAttached>
          <IconButton
            icon={<Icon as={MdLink} />}
            onClick={setLink}
            isActive={editor.isActive('link')}
            aria-label='Link'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdImage} />}
            onClick={addImage}
            aria-label='Image'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
        </ButtonGroup>

        <Divider orientation='vertical' height='24px' />

        {/* Undo/Redo */}
        <ButtonGroup size='sm' isAttached>
          <IconButton
            icon={<Icon as={MdUndo} />}
            onClick={() => editor.chain().focus().undo().run()}
            isDisabled={!editor.can().undo()}
            aria-label='Undo'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
          <IconButton
            icon={<Icon as={MdRedo} />}
            onClick={() => editor.chain().focus().redo().run()}
            isDisabled={!editor.can().redo()}
            aria-label='Redo'
            variant='ghost'
            _hover={{ bg: hoverBg }}
            _active={{ bg: activeBg }}
          />
        </ButtonGroup>
      </Flex>

      {/* Editor Content */}
      <Box
        p='20px'
        minH='300px'
        maxH='600px'
        overflowY='auto'
        sx={{
          '& .ProseMirror': {
            outline: 'none',
            minHeight: '300px',
            color: textColor,
            fontSize: 'sm',
            '& p.is-editor-empty:first-child::before': {
              color: placeholderColor,
              content: `attr(data-placeholder)`,
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontWeight: 'bold',
              marginTop: '1em',
              marginBottom: '0.5em',
            },
            '& h1': { fontSize: '2em' },
            '& h2': { fontSize: '1.5em' },
            '& h3': { fontSize: '1.25em' },
            '& ul, & ol': {
              paddingLeft: '1.5em',
            },
            '& blockquote': {
              borderLeft: `4px solid ${borderColor}`,
              paddingLeft: '1em',
              fontStyle: 'italic',
            },
            '& code': {
              bg: codeBg,
              padding: '2px 4px',
              borderRadius: '4px',
              fontSize: '0.9em',
            },
            '& pre': {
              bg: codeBg,
              padding: '1em',
              borderRadius: '8px',
              overflow: 'auto',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
            },
            '& a': {
              color: linkColor,
              textDecoration: 'underline',
            },
          },
        }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
